// StripeConnectSettings.js
import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Form, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getDatabase, ref, get, update } from 'firebase/database';
import { useAuth } from '../../contexts/AuthContext';
import { ThreeDots } from 'react-loader-spinner';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

export default function StripeConnectSettings({ userData, metaData }) {
  const auth = useAuth();
  const [stripeConnectData, setStripeConnectData] = useState(null);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [accountStatus, setAccountStatus] = useState(null);
  const [taxInformation, setTaxInformation] = useState(null);
  const [taxForm, setTaxForm] = useState({
    taxIdType: 'SSN',
    taxId: ''
  });
  const [showTaxId, setShowTaxId] = useState(false);
  const serverUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    const fetchStripeConnectData = async () => {
      try {
        if (metaData && metaData.taxInformation) {
          setTaxInformation(metaData.taxInformation);
          setTaxForm((prevForm) => ({
            ...prevForm,
            taxIdType: metaData.taxInformation.taxIdType || 'SSN'
          }));
        }

        if (metaData && metaData.payoutAccounts && metaData.payoutAccounts.Stripe) {
          setStripeConnectData(metaData.payoutAccounts.Stripe);
          
          // Check the account status from Stripe
          if (metaData.payoutAccounts.Stripe.accountId) {
            const response = await fetch(`${serverUrl}/stripe-connect/account-status/${auth.currentUser.uid}`);
            const data = await response.json();
            if (data.success) {
              setAccountStatus(data);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching Stripe Connect data:', error);
      }
    };

    fetchStripeConnectData();
  }, [metaData, auth.currentUser.uid, serverUrl]);

  const handleTaxFormChange = (e) => {
    const { name, value } = e.target;
    setTaxForm((prevForm) => ({
      ...prevForm,
      [name]: value
    }));
    if (error) {
      setError('');
    }
  };

  const cleanTaxId = (taxId) => taxId.replace(/\D/g, '');

  const hasSavedTaxInformation = Boolean(
    taxInformation &&
      taxInformation.taxIdType &&
      (taxInformation.taxId || taxInformation.taxIdLast4)
  );

  const saveTaxInformation = async () => {
    const cleanedTaxId = cleanTaxId(taxForm.taxId);

    if (!hasSavedTaxInformation || cleanedTaxId) {
      if (cleanedTaxId.length !== 9) {
        throw new Error(`Please enter a valid 9-digit ${taxForm.taxIdType}.`);
      }

      const database = getDatabase();
      const userRef = ref(database, `ownerMeta/${auth.currentUser.uid}`);
      const updatedTaxInformation = {
        taxIdType: taxForm.taxIdType,
        taxId: cleanedTaxId,
        taxIdLast4: cleanedTaxId.slice(-4),
        updatedAt: new Date().toISOString()
      };

      await update(userRef, {
        taxInformation: updatedTaxInformation
      });
      setTaxInformation(updatedTaxInformation);
    }
  };

  const openConnectModal = () => {
    setShowConnectModal(true);
  };

  const closeConnectModal = () => {
    setShowConnectModal(false);
  };

  const connectStripeAccount = async () => {
    try {
      setLoading(true);
      setError('');

      await saveTaxInformation();
      
      const response = await fetch(`${serverUrl}/create-connect-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId: auth.currentUser.uid })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Redirect to Stripe's onboarding URL
        window.location.href = data.onboardingUrl;
      } else {
        setError(data.error || 'Failed to create Stripe Connect account');
      }
    } catch (err) {
      setError(err.message || 'Error connecting to Stripe');
    } finally {
      setLoading(false);
    }
  };

  const generateLoginLink = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`${serverUrl}/stripe-connect/login-link/${auth.currentUser.uid}`);
      const data = await response.json();
      
      if (data.success) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to create login link');
      }
    } catch (err) {
      setError('Error creating login link: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const reconnectStripeAccount = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!accountStatus || !accountStatus.accountLink) {
        // Refresh the account status to get a new account link
        const response = await fetch(`${serverUrl}/stripe-connect/account-status/${auth.currentUser.uid}`);
        const data = await response.json();
        
        if (data.success && data.accountLink) {
          window.location.href = data.accountLink;
        } else {
          setError('Unable to get onboarding link');
        }
      } else {
        window.location.href = accountStatus.accountLink;
      }
    } catch (err) {
      setError('Error reconnecting Stripe account: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const disconnectStripeAccount = async () => {
    try {
      setLoading(true);
      setError('');
      
      // This would require a backend endpoint to disconnect the account
      const response = await fetch(`${serverUrl}/disconnect-stripe-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId: auth.currentUser.uid })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setStripeConnectData(null);
        setAccountStatus(null);
        setSuccess('Stripe Connect account disconnected successfully');
        
        // Also update local state
        const database = getDatabase();
        const userRef = ref(database, `ownerMeta/${auth.currentUser.uid}`);
        await update(userRef, {
          'payoutAccounts/Stripe': null
        });
        
        setTimeout(() => {
          setSuccess('');
          closeConnectModal();
        }, 2000);
      } else {
        setError(data.error || 'Failed to disconnect Stripe account');
      }
    } catch (err) {
      setError('Error disconnecting Stripe account: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="">
          <h5>Stripe Connect Account</h5>
          <span>
            Receive payouts directly to your bank account via Stripe. Connect your account to manage your payout information.
          </span>
        </div>
        <div className="row m-t-xxl payout mt-4 p-0">
          <div className="col-md-12">
            <div className="row payoutRow justify-content-between">
              <div className="col-6 p-4 d-flex">
    <h3>STRIPE</h3>
              </div>
              <div className="col-md-2 p-4 d-flex flex-column">
                {!stripeConnectData ? (
                  <button className="btn btn-primary mb-1" onClick={openConnectModal}>
                    Connect
                  </button>
                ) : (
                  <>
                    <button className="btn btn-primary mb-1" onClick={generateLoginLink}>
                      Manage
                    </button>
                    {accountStatus && !accountStatus.isFullyEnabled && (
                      <button className="btn btn-warning mt-1 mb-1" onClick={reconnectStripeAccount}>
                        Complete Setup
                      </button>
                    )}
                    <button className="btn btn-outline-danger mt-1" onClick={openConnectModal}>
                      Disconnect
                    </button>
                  </>
                )}
              </div>
            </div>
            
            {stripeConnectData && accountStatus && (
              <div className="mt-3 p-3 bg-light rounded">
                <h6>Account Status</h6>
                <div className="d-flex align-items-center mt-2">
                  <span className="me-2">Status:</span>
                  <span className={`badge ${accountStatus.isFullyEnabled ? 'bg-success' : 'bg-warning'}`}>
                    {accountStatus.isFullyEnabled ? 'Active' : 'Setup Required'}
                  </span>
                </div>
                
                {!accountStatus.isFullyEnabled && accountStatus.requirements && accountStatus.requirements.currentlyDue.length > 0 && (
                  <div className="mt-3">
                    <small className="text-muted">Please complete the following requirements:</small>
                    <ul className="mt-1 small">
                      {accountStatus.requirements.currentlyDue.map((requirement, index) => (
                        <li key={index}>{requirement.split('_').join(' ')}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Connect/Disconnect Modal */}
      <Modal centered show={showConnectModal} onHide={closeConnectModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {!stripeConnectData ? 'Connect with Stripe' : 'Disconnect Stripe Account'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!stripeConnectData ? (
            <>
              <p>
                Connect your Stripe account to receive payments directly to your bank account.
                Stripe will handle your identity verification and payment information.
              </p>
              <Alert variant="info" className="mt-3">
                BBR uses your SSN or EIN for annual owner tax reporting. Please provide one tax identifier before continuing to Stripe.
              </Alert>
              {hasSavedTaxInformation && (
                <Alert variant="success" className="mt-3">
                  Tax information on file: {taxInformation.taxIdType} ending in {taxInformation.taxIdLast4 || String(taxInformation.taxId).slice(-4)}.
                </Alert>
              )}
              <Form className="mt-3">
                <Form.Group className="mb-3" controlId="taxIdType">
                  <Form.Label>Tax identifier type</Form.Label>
                  <Form.Select
                    name="taxIdType"
                    value={taxForm.taxIdType}
                    onChange={handleTaxFormChange}
                  >
                    <option value="SSN">Individual - SSN</option>
                    <option value="EIN">Business - EIN</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3" controlId="taxId">
                  <Form.Label>{taxForm.taxIdType}</Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type={showTaxId ? 'text' : 'password'}
                      inputMode="numeric"
                      name="taxId"
                      value={taxForm.taxId}
                      onChange={handleTaxFormChange}
                      placeholder={
                        hasSavedTaxInformation
                          ? `Leave blank to keep ${taxInformation.taxIdType} on file`
                          : `Enter 9-digit ${taxForm.taxIdType}`
                      }
                      style={{ paddingRight: '2.75rem' }}
                    />
                    <button
                      type="button"
                      aria-label={showTaxId ? `Hide ${taxForm.taxIdType}` : `Show ${taxForm.taxIdType}`}
                      title={showTaxId ? `Hide ${taxForm.taxIdType}` : `Show ${taxForm.taxIdType}`}
                      onClick={() => setShowTaxId((visible) => !visible)}
                      className="btn btn-link position-absolute top-50 end-0 translate-middle-y text-muted"
                      style={{
                        border: 0,
                        boxShadow: 'none',
                        padding: '0.375rem 0.75rem',
                        lineHeight: 1
                      }}
                    >
                      {showTaxId ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  <Form.Text className="text-muted">
                    Use one field only: SSN for individuals or EIN for businesses.
                  </Form.Text>
                </Form.Group>
              </Form>
              <p>
                After connecting, you'll be redirected to Stripe to complete your account setup.
              </p>
            </>
          ) : (
            <>
              <p>
                Are you sure you want to disconnect your Stripe account?
                You won't be able to receive payouts through Stripe until you reconnect.
              </p>
              <p className="text-danger">
                This action will not affect any pending payments but will prevent future payouts.
              </p>
            </>
          )}
          
          {error && (
            <Alert variant="danger" className="mt-3">
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert variant="success" className="mt-3">
              {success}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeConnectModal}>
            Cancel
          </Button>
          
          {!stripeConnectData ? (
            <Button 
              variant="primary" 
              onClick={connectStripeAccount}
              disabled={loading}
            >
              {loading ? (
                <ThreeDots
                  visible={true}
                  height="22"
                  width="22"
                  color="#fff"
                  radius="20"
                  ariaLabel="three-dots-loading"
                />
              ) : (
                'Connect with Stripe'
              )}
            </Button>
          ) : (
            <Button 
              variant="danger" 
              onClick={disconnectStripeAccount}
              disabled={loading}
            >
              {loading ? (
                <ThreeDots
                  visible={true}
                  height="22"
                  width="22"
                  color="#fff"
                  radius="20"
                  ariaLabel="three-dots-loading"
                />
              ) : (
                'Disconnect'
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}
